import type { Match, Team, TeamId } from '@/core/schemas';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { roundLabel, scoreLabel, sideLabel } from '@/features/matches/matchPresentation';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';
import { cn } from '@/shared/utils/cn';

export function KnockoutPage() {
  usePageTitle('Turnierbaum');
  const { matches, myTeam, lookup, phases, isLoading, errorMessage } = useTournamentData();

  if (isLoading) {
    return <LoadingScreen label="Turnierbaum wird geladen …" />;
  }

  if (errorMessage !== null) {
    return <PageState title="Turnierbaum nicht verfügbar" description={errorMessage} />;
  }

  const bracketPhases = phases.filter((phase) =>
    phase.matches.some((match) => match.bracketSlot !== null),
  );

  if (bracketPhases.length === 0) {
    return (
      <Alert tone="info">
        Die KO-Phase beginnt, sobald die Gruppenphase abgeschlossen ist. Der Baum erscheint dann
        hier automatisch.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <h1 className="text-2xl font-bold tracking-tight">Turnierbaum</h1>

      {bracketPhases.map((phase) => (
        <section key={phase.phaseId} className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{phase.label}</h2>
          <Bracket matches={phase.matches} lookup={lookup} highlightTeamId={myTeam?.id} />
        </section>
      ))}

      <p className="text-fg-subtle text-xs">Insgesamt {matches.length} Spiele im Turnier.</p>
    </div>
  );
}

interface BracketProps {
  matches: readonly Match[];
  lookup: (teamId: TeamId) => Team | undefined;
  highlightTeamId?: TeamId | undefined;
}

/**
 * Rounds as columns, growing towards the final.
 *
 * Horizontal scrolling is deliberate: on a phone a bracket cannot be made to
 * fit without becoming unreadable, and scrolling beats squeezing.
 */
function Bracket({ matches, lookup, highlightTeamId }: BracketProps) {
  // Grouped by `roundKey`, not by the numeric round: the third-place match sits
  // in the same round as the final but is a different round entirely, and
  // putting it under the heading "Finale" would be plainly wrong.
  const columns = new Map<string, Match[]>();

  for (const match of matches) {
    if (match.bracketSlot === null) {
      continue;
    }
    const key = String(match.roundKey);
    columns.set(key, [...(columns.get(key) ?? []), match]);
  }

  const ordered = [...columns.entries()].sort(
    ([, a], [, b]) => (a[0]?.order ?? 0) - (b[0]?.order ?? 0),
  );

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max gap-4">
        {ordered.map(([key, roundMatches]) => (
          <div key={key} className="flex w-56 shrink-0 flex-col justify-around gap-3">
            <h3 className="text-fg-subtle text-xs tracking-widest uppercase">
              {roundTitle(roundMatches)}
            </h3>

            {roundMatches
              .sort((a, b) => (a.bracketSlot?.index ?? 0) - (b.bracketSlot?.index ?? 0))
              .map((match) => (
                <BracketMatch
                  key={match.id}
                  match={match}
                  lookup={lookup}
                  highlightTeamId={highlightTeamId}
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function roundTitle(matches: readonly Match[]): string {
  const key = matches[0]?.roundKey;
  return key === undefined ? '' : roundLabel(String(key));
}

interface BracketMatchProps {
  match: Match;
  lookup: (teamId: TeamId) => Team | undefined;
  highlightTeamId?: TeamId | undefined;
}

function BracketMatch({ match, lookup, highlightTeamId }: BracketMatchProps) {
  const winner = match.result?.winnerTeamId ?? null;

  const renderSide = (side: Match['home'], legs: number | undefined) => {
    const isWinner = side.kind === 'team' && side.teamId === winner;
    const isOwn = side.kind === 'team' && side.teamId === highlightTeamId;

    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span
          className={cn(
            'truncate text-sm',
            side.kind === 'pending' && 'text-fg-subtle italic',
            isWinner && 'font-semibold',
            isOwn && 'text-brand font-semibold',
          )}
        >
          {sideLabel(side, lookup)}
        </span>
        <span className="text-fg-muted shrink-0 font-mono text-xs">{legs ?? ''}</span>
      </div>
    );
  };

  return (
    <div className="border-line bg-surface divide-line divide-y overflow-hidden rounded-lg border">
      {renderSide(match.home, match.result?.score.legs.home)}
      {renderSide(match.away, match.result?.score.legs.away)}
      {match.status === 'conflict' && (
        <p className="bg-danger-subtle text-danger px-3 py-1 text-xs">Konflikt</p>
      )}
      {scoreLabel(match) === '' && match.boardNumber !== null && (
        <p className="text-fg-subtle px-3 py-1 text-xs">Board {match.boardNumber}</p>
      )}
    </div>
  );
}
